import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import { API_BASE_URL, ENDPOINTS } from '../../config';
import 'notyf/notyf.min.css';
import { Notyf } from 'notyf';
import { GoogleLogin } from '@react-oauth/google';
import { PublicClientApplication } from "@azure/msal-browser";

const msalConfig = {
    auth: {
        clientId: "<YOUR_MICROSOFT_CLIENT_ID>",
        authority: "https://login.microsoftonline.com/common",
        redirectUri: window.location.origin,
    },
};
const msalInstance = new PublicClientApplication(msalConfig);
const notyf = new Notyf({
    position: {
        x: 'right',
        y: 'top'
    },
    ripple: true,
    dismissible: true,
});
export default function Registration() {
    const [email, setEmail] = useState('');
    const [fullname, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [, setCookie] = useCookies(['token']);

    const togglePassword = () => setShowPassword((prev) => !prev);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic form validation
        if (!fullname || !email || !password) {
            notyf.error("All fields are required.");
            return;
        }

        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            notyf.error("Please enter a valid email address.");
            return;
        }

        // Password validation (min 8 chars, upper, lower, number, special)
        const passwordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(password)) {
            notyf.error(
                "Password must be at least 8 characters, include upper, lower, number, and special char."
            );
            return;
        }

        // Split full name into first/last
        const [first_name, ...rest] = fullname.trim().split(" ");
        const last_name = rest.join(" ");

        const userData = {
            first_name: first_name || "",
            last_name: last_name || "",
            email: email.trim(),
            password,
        };

        try {
            // Signup API call
            const response = await axios.post(
                `${API_BASE_URL}${ENDPOINTS.AUTH.SIGNUP}`,
                userData,
                {
                    headers: { "Content-Type": "application/json" },
                }
            );

            const data = response.data;

            if (!data?.access_token) {
                notyf.error("Signup failed. Please try again.");
                return;
            }

            // Store token in secure cookie
            setCookie("token", data.access_token, {
                path: "/",
                secure: true,
                sameSite: "strict",
                maxAge: 60 * 60 * 24, // 1 day
            });

            // Fetch chats after signup
            const chatsRes = await axios.get(
                `${API_BASE_URL}${ENDPOINTS.CHATS.GET_ALL}`,
                {
                    headers: { Authorization: `Bearer ${data.access_token}` },
                }
            );

            let chatId;
            if (!chatsRes.data || chatsRes.data.length === 0) {
                // Create a default chat if none exist
                const newChat = await axios.post(
                    `${API_BASE_URL}${ENDPOINTS.CHATS.CREATE}`,
                    { title: "Ask Me Anything", messages: [] },
                    { headers: { Authorization: `Bearer ${data.access_token}` } }
                );
                chatId = newChat.data.chat_id;
            } else {
                chatId = chatsRes.data[0].chat_id;
            }

            // Navigate to chat
            navigate(`/c/${chatId}`);
            notyf.success("Signup successful!");
        } catch (err) {
            console.error("Signup error:", err);

            if (err.response) {
                // Server responded but with error
                notyf.error(err.response.data.detail || "Signup failed. Try again.");
            } else if (err.request) {
                // No response from server
                notyf.error("No response from server. Please check your connection.");
            } else {
                // Something else happened
                notyf.error("Unexpected error occurred. Please try again.");
            }
        }
    };



    const handleGoogleSuccess = async (response) => {
        console.log("Google login success", response);
        const googleToken = response.credential; // Google OAuth token

        try {
            const googleAuthResponse = await axios.post(
                `${API_BASE_URL}${ENDPOINTS.AUTH.LOGIN}`,
                { token: googleToken, provider: "google" },
                { headers: { 'Content-Type': 'application/json' } }
            );

            setCookie('token', googleAuthResponse.data.access_token, {
                path: '/',
                secure: true,
                sameSite: 'strict',
                maxAge: 24 * 60 * 60,
            });

            const chatResponse = await axios.get(
                `${API_BASE_URL}${ENDPOINTS.CHATS.GET_ALL}`,
                {
                    headers: { Authorization: `Bearer ${googleAuthResponse.data.access_token}` },
                }
            );

            if (!chatResponse.data[0]) {
                const newChat = await axios.post(
                    `${API_BASE_URL}${ENDPOINTS.CHATS.CREATE}`,
                    { title: 'Ask Me Anything', messages: [{ content: '', sender: '' }] },
                    { headers: { Authorization: `Bearer ${googleAuthResponse.data.access_token}` } }
                );
                navigate(`/c/${newChat.data.chat_id}`);
            } else {
                navigate(`/c/${chatResponse.data[0].chat_id}`);
            }

            notyf.success('Google login successful!');
        } catch (error) {
            console.log(error)
            const errorMessage = error.response?.data?.detail || 'Google login failed';
            setError(errorMessage);
            notyf.error(errorMessage);
        }
    };

    const handleMicrosoftLogin = async () => {
        try {
            const loginResponse = await msalInstance.loginPopup({
                scopes: ["user.read"], // Needed for Graph API
            });

            const msToken = loginResponse.accessToken;

            // Send Microsoft token to backend
            const msAuthResponse = await axios.post(
                `${API_BASE_URL}${ENDPOINTS.AUTH.LOGIN}`,
                { token: msToken, provider: "microsoft" },  // 👈 same format as Google
                { headers: { "Content-Type": "application/json" } }
            );

            // Save JWT
            setCookie("token", msAuthResponse.data.access_token, {
                path: "/",
                secure: true,
                sameSite: "strict",
                maxAge: 24 * 60 * 60,
            });

            // Fetch chats
            const chatResponse = await axios.get(
                `${API_BASE_URL}${ENDPOINTS.CHATS.GET_ALL}`,
                {
                    headers: {
                        Authorization: `Bearer ${msAuthResponse.data.access_token}`,
                    },
                }
            );

            if (!chatResponse.data[0]) {
                const newChat = await axios.post(
                    `${API_BASE_URL}${ENDPOINTS.CHATS.CREATE}`,
                    { title: "Ask Me Anything", messages: [{ content: "", sender: "" }] },
                    { headers: { Authorization: `Bearer ${msAuthResponse.data.access_token}` } }
                );
                navigate(`/c/${newChat.data.chat_id}`);
            } else {
                navigate(`/c/${chatResponse.data[0].chat_id}`);
            }

            notyf.success("Microsoft login successful!");
        } catch (err) {
            console.error("Microsoft login failed", err);
            notyf.error("Microsoft login failed. Please try again.");
        }
    };
    // Handle Google login failure
    const handleGoogleFailure = (error) => {
        console.error('Google login failed:', error);
        setError('Google login failed. Please try again.');
        notyf.error('Google login failed. Please try again.');
    };

    return (
        <div className="auth-body login">
            <div className="auth-background">
                <div className="bg-pattern"></div>
                <div className="floating-elements">
                    <div className="floating-element"><i className="fas fa-brain"></i></div>
                    <div className="floating-element"><i className="fas fa-robot"></i></div>
                    <div className="floating-element"><i className="fas fa-chart-line"></i></div>
                    <div className="floating-element"><i className="fas fa-image"></i></div>
                </div>
            </div>

            <div className="auth-container">
                <div className="auth-card">
                    <div className="auth-header">
                        <div className="brand-logo">
                            <h1><img src="assets/img/hola.png" alt="logo" className="logo" /></h1>
                        </div>
                        <p>Sign Up to access your AI Assistant</p>
                    </div>

                    <form className="auth-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <div className="input-group">
                                <span className="input-group-text"><i className="fas fa-envelope"></i></span>
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <div className="input-group">
                                <span className="input-group-text"><i className="fas fa-user"></i></span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter your full name"
                                    value={fullname}
                                    onChange={(e) => setFullName(e.target.value)}
                                    required
                                    autoComplete="off"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="input-group position-relative">
                                <span className="input-group-text"><i className="fas fa-lock"></i></span>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-control"
                                    placeholder="Create a password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="off"
                                />
                                <button type="button" className="password-toggle" onClick={togglePassword}>
                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                        </div>

                        {error && <div className="text-danger mb-2">{error}</div>}

                        <button type="submit" className="btn btn-primary btn-auth">
                            <i className="fas fa-user-plus"></i> Create Account
                        </button>
                    </form>

                    <div className="auth-divider"><span>or Sign Up With</span></div>

                    <div className="social-auth">
                        <button className="social-btn">
                            {/* <i className="fab fa-google"></i>Continue with Google */}
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={handleGoogleFailure}
                            />
                        </button>
                        {/* Microsoft OAuth Button */}
                        <button className="social-btn" onClick={handleMicrosoftLogin}>
                            <i className="fab fa-microsoft"></i> Sign Up With Microsoft
                        </button>
                    </div>

                    <div className="auth-footer">
                        Already have an account? <Link to="/login">Sign in here</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
